/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Button, Grid, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import { CheckIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import EnterpriseSkillPathwayCourseActionButton from 'bundles/enterprise-learning-path-description-page/components/EnterpriseSkillPathwayCourseActionButton';
import SkillExpressionsDialog from 'bundles/enterprise-learning-path-description-page/components/syllabus/SkillExpressionsDialog';
import SkillPathwayModuleV2 from 'bundles/enterprise-learning-path-description-page/components/syllabus/SkillPathwayModuleV2';
import {
  type VspCourseData,
  getModulesDataFromVspCourseData,
} from 'bundles/enterprise-learning-path-description-page/utils/vspXdpV2Utils';

import _t from 'i18n!nls/enterprise-learning-path-description-page';

const styles = {
  skillItem: css`
    display: flex;
    align-items: flex-start;
    gap: 0;
    margin-bottom: var(--cds-spacing-50);
  `,
  skillText: css`
    flex: 1;
    margin: 0;
  `,
  separator: css`
    display: flex;
    align-items: center;
  `,

  gridContainer: css`
    margin-top: var(--cds-spacing-150);
  `,

  button: css`
    margin-top: var(--cds-spacing-100);
  `,
};

type Props = {
  skillExpressions: string[];
  vspCourseData: VspCourseData | undefined;
  // Enrollment props for EnterpriseSkillPathwayCourseActionButton
  isEnrolledInPathway?: boolean;
  isEnrolledInCourse?: boolean;
  isProgramMember?: boolean;
  courseId?: string;
  courseSlug?: string;
  pathwayId?: string;
  pathwayCollectionId?: string;
  programId?: string;
  programSlug?: string;
  thirdPartyOrganizationSlug?: string;
  activateProgramAccessModal?: () => void;
};

const SKILL_EXPRESSIONS_BASE_DISPLAY_COUNT = 4;

const MODULES_BASE_DISPLAY_COUNT = 2;

const SkillPathwayCourseContentV2 = ({
  skillExpressions,
  vspCourseData,
  isEnrolledInPathway,
  isEnrolledInCourse,
  isProgramMember,
  courseId,
  courseSlug,
  pathwayId,
  pathwayCollectionId,
  programId,
  programSlug,
  thirdPartyOrganizationSlug,
  activateProgramAccessModal,
}: Props) => {
  const [showSkillExpressionsDialog, setShowSkillExpressionsDialog] = React.useState(false);
  const [showAllModules, setShowAllModules] = React.useState(false);
  const isMobile = useMediaQuery(breakpoints.down('sm'));
  const moduleClickHandlers = React.useRef<Map<number, () => Promise<void>>>(new Map());
  const track = useTracker();

  if (skillExpressions.length === 0) {
    return null;
  }

  const displayedSkills = skillExpressions.slice(0, SKILL_EXPRESSIONS_BASE_DISPLAY_COUNT);

  const midpoint = Math.ceil(displayedSkills.length / 2);
  const firstHalf = displayedSkills.slice(0, midpoint);
  const secondHalf = displayedSkills.slice(midpoint);

  const renderSkillItem = (skillExpression: string) => (
    <Grid container key={skillExpression} direction="row" wrap="nowrap" spacing={8}>
      <Grid item>
        <CheckIcon size="medium" />
      </Grid>
      <Grid item>
        <Typography2
          variant={isMobile ? 'bodySecondary' : 'bodyPrimary'}
          component="p"
          color="body"
          css={styles.skillText}
        >
          {skillExpression}
        </Typography2>
      </Grid>
    </Grid>
  );

  const modulesData = getModulesDataFromVspCourseData(vspCourseData);

  const getLinkPropsLink = (moduleIndex: number, diagnosticItemId?: string) => {
    if (!vspCourseData?.slug) {
      return '#';
    }

    // Check if we have enrollment data to enable click handlers
    const hasEnrollmentData =
      courseId &&
      courseSlug &&
      programId &&
      thirdPartyOrganizationSlug &&
      pathwayId &&
      isEnrolledInPathway !== undefined &&
      isEnrolledInCourse !== undefined &&
      isProgramMember !== undefined;

    if (hasEnrollmentData) {
      return {
        href: '#',
        onClick: (e: React.MouseEvent) => {
          const onClickHandler = moduleClickHandlers.current.get(moduleIndex);
          if (onClickHandler) {
            track('click_button', {
              button: {
                name: 'skill_pathway_skill_area_module_click',
              },
            });
            e.preventDefault();
            onClickHandler();
          }
        },
      };
    }

    return diagnosticItemId
      ? `/learn/${vspCourseData.slug}/assignment-submission/${diagnosticItemId}`
      : `/learn/${vspCourseData.slug}/home/module/${moduleIndex + 1}`;
  };

  return (
    <div>
      {isMobile ? (
        <div>{displayedSkills.map(renderSkillItem)}</div>
      ) : (
        <Grid container spacing={24}>
          <Grid item xs={12} md={6}>
            {firstHalf.map(renderSkillItem)}
          </Grid>
          <Grid item xs={12} md={6}>
            {secondHalf.map(renderSkillItem)}
          </Grid>
        </Grid>
      )}
      {skillExpressions.length > SKILL_EXPRESSIONS_BASE_DISPLAY_COUNT && (
        <Button variant="ghost" size="small" onClick={() => setShowSkillExpressionsDialog(true)}>
          <FormattedMessage
            message={_t('Show {remainingCount} more skills')}
            remainingCount={skillExpressions.length - SKILL_EXPRESSIONS_BASE_DISPLAY_COUNT}
          />
        </Button>
      )}
      {showSkillExpressionsDialog && (
        <SkillExpressionsDialog
          skillExpressions={skillExpressions}
          onClose={() => setShowSkillExpressionsDialog(false)}
        />
      )}
      <Grid container direction="column" spacing={12} css={styles.gridContainer}>
        {(showAllModules ? modulesData : modulesData.slice(0, MODULES_BASE_DISPLAY_COUNT)).map(
          (module, moduleIndex: number) => {
            const linkProps = getLinkPropsLink(moduleIndex, module.diagnosticItemId);
            const moduleUrl = vspCourseData?.slug ? `/learn/${vspCourseData.slug}/home/module/${moduleIndex + 1}` : '#';
            const diagnosticUrl =
              module.diagnosticItemId && vspCourseData?.slug
                ? `/learn/${vspCourseData.slug}/assignment-submission/${module.diagnosticItemId}`
                : moduleUrl;
            return (
              <Grid item key={module.name}>
                {courseId &&
                  courseSlug &&
                  programId &&
                  thirdPartyOrganizationSlug &&
                  pathwayId &&
                  isEnrolledInPathway !== undefined &&
                  isEnrolledInCourse !== undefined &&
                  isProgramMember !== undefined && (
                    <EnterpriseSkillPathwayCourseActionButton
                      hiddenButton
                      isEnrolledInPathway={isEnrolledInPathway}
                      isEnrolledInCourse={isEnrolledInCourse}
                      isProgramMember={isProgramMember}
                      courseId={courseId}
                      courseSlug={courseSlug}
                      programId={programId}
                      pathwayId={pathwayId}
                      pathwayCollectionId={pathwayCollectionId}
                      programSlug={programSlug}
                      thirdPartyOrganizationSlug={thirdPartyOrganizationSlug}
                      activateProgramAccessModal={activateProgramAccessModal}
                      customRedirectUrl={module.diagnosticItemId ? diagnosticUrl : moduleUrl}
                      onClickHandler={(onClick) => {
                        moduleClickHandlers.current.set(moduleIndex, onClick);
                      }}
                    />
                  )}
                <SkillPathwayModuleV2
                  title={module.name}
                  partners={module.partners}
                  toolsAndLanguages={module.toolsAndLanguages}
                  timeDuration={module.duration}
                  durationUnit={module.durationUnit}
                  linkProps={linkProps}
                />
              </Grid>
            );
          }
        )}
      </Grid>
      {modulesData.length > MODULES_BASE_DISPLAY_COUNT &&
        (showAllModules ? (
          <Button variant="ghost" size="small" onClick={() => setShowAllModules(false)} css={styles.button}>
            {_t('Show fewer modules')}
          </Button>
        ) : (
          <Button variant="ghost" size="small" onClick={() => setShowAllModules(true)} css={styles.button}>
            <FormattedMessage
              message={_t('Show {remainingCount} more modules')}
              remainingCount={modulesData.length - MODULES_BASE_DISPLAY_COUNT}
            />
          </Button>
        ))}
    </div>
  );
};

export default SkillPathwayCourseContentV2;
